import { Pencil, Trash2, FileText } from "lucide-react";
import { formatCurrency } from "@/lib/data";
import EmptyState from "../../components/EmptyState";
import { Client } from "../types";
import { Users } from "lucide-react";

interface ClientsTableProps {
  clients: Client[];
  isFiltered: boolean;
  isStaff: boolean;
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
  onLedger: (client: Client) => void;
  onClearFilter: () => void;
  onAddClient: () => void;
}

export default function ClientsTable({
  clients,
  isFiltered,
  isStaff,
  onEdit,
  onDelete,
  onLedger,
  onClearFilter,
  onAddClient,
}: ClientsTableProps) {
  if (clients.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No clients yet"
        description="Add your first client to start tracking events and payments."
        isFiltered={isFiltered}
        onClearFilter={onClearFilter}
        action={{ label: "Add Client", onClick: onAddClient }}
      />
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-100">
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Client</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Contact</th>
              <th className="text-right px-5 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Events</th>
              <th className="text-right px-5 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Revenue</th>
              <th className="text-right px-5 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Balance</th>
              <th className="px-5 py-3.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {clients.map(client => (
              <tr key={client.id} className="hover:bg-stone-50/60 transition-colors group">
                {/* Client identity */}
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-sm font-bold text-amber-600 flex-shrink-0">
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-stone-800 truncate">{client.name}</p>
                      {client.company_name && (
                        <p className="text-xs text-stone-400 truncate">{client.company_name}</p>
                      )}
                    </div>
                  </div>
                </td>

                {/* Contact */}
                <td className="px-5 py-4">
                  <div className="text-sm space-y-0.5">
                    {client.phone && <div className="text-stone-600">{client.phone}</div>}
                    {client.email && <div className="text-xs text-stone-400">{client.email}</div>}
                    {!client.phone && !client.email && <span className="text-stone-300 text-xs">—</span>}
                  </div>
                </td>

                {/* Events */}
                <td className="px-5 py-4 text-right">
                  <span className={`text-sm font-medium ${client.events_count ? "text-stone-700" : "text-stone-300"}`}>
                    {client.events_count || 0}
                  </span>
                </td>

                {/* Revenue */}
                <td className="px-5 py-4 text-right">
                  <span className="text-sm font-semibold text-stone-700">
                    {formatCurrency(client.total_revenue || 0)}
                  </span>
                </td>

                {/* Balance */}
                <td className="px-5 py-4 text-right">
                  {client.balance && client.balance > 0 ? (
                    <span className="text-sm font-semibold text-amber-600">{formatCurrency(client.balance)}</span>
                  ) : (
                    <span className="text-sm text-emerald-500 font-medium">Cleared</span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-5 py-4">
                  <div className="flex justify-end items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => onLedger(client)}
                      className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
                      title="View Ledger"
                      aria-label="View client ledger"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(client)}
                      className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 transition-colors"
                      title="Edit Client"
                      aria-label="Edit client"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {!isStaff && (
                      <button
                        onClick={() => onDelete(client)}
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete Client"
                        aria-label="Delete client"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
